import { FunctionComponent, useState } from 'react'
import { getSearchVenue } from '@/libs/helpers/apiClient'
import { useDebounce } from '@/libs/helpers/hooks/useDebounce'
import { parseHTMLEntities, OrderMethod } from '@/libs/helpers/utils'
import { useGoogleMaps, AutocompleteOption } from '@/providers/googleMaps'
import { useSearch, SearchVenue } from '@/providers/state/search'
import { InputAutocomplete, Suggestion } from '@/tbui'

const VenueAutocompleteComponent: FunctionComponent<{
  label?: string
  orderMethod: OrderMethod
}> = ({ label, orderMethod }) => {
  // HOOKS
  const [{ orderingVenue }, { setOrderingVenue, setOrderMethod }] = useSearch()
  const [{ orderingAddress }] = useGoogleMaps()

  // STATES
  const [controlledValue, setControlledValue] = useState<string>(orderingVenue || '')
  const [venueAutocompleteOptions, setVenueAutocompleteOptions] = useState<AutocompleteOption[]>([])

  // HANDLERS
  const handleSelection = (selectedSuggestion: Suggestion): void => {
    if (selectedSuggestion != null) {
      setOrderMethod(orderMethod)
    }
    setOrderingVenue(orderMethod, selectedSuggestion?.value)
  }

  const handleVenueSuggestions = (data: SearchVenue[]): void => {
    const options = data.map((venue) => {
      const parsedVenueName = parseHTMLEntities(venue.venueName)
      return {
        primaryText: parsedVenueName,
        label: parsedVenueName,
        id: venue.venueXRefID,
      }
    })
    setVenueAutocompleteOptions(options)
  }

  // debounced to 250ms to avoid calling backend on every key stroke
  const asyncSetVenueAutocompleteOptions = useDebounce(
    async (inputValue: string): Promise<void> => {
      setControlledValue(inputValue)
      if (inputValue.length >= 3) {
        const searchResults = await getSearchVenue({
          params: {
            consumerLongitude: orderingAddress?.lng,
            consumerLatitude: orderingAddress?.lat,
            query: inputValue,
            orderMethod,
          },
        })

        handleVenueSuggestions(searchResults.data)
      }
    },
    250
  )

  const mapAutocompleteOptions = (options: AutocompleteOption[]): Suggestion[] => {
    return options?.map((option) => ({
      label: option.primaryText,
      value: option.label,
      key: option.id,
    }))
  }

  // RENDERER
  return (
    <div data-test="input-autocomplete-wrapper">
      <InputAutocomplete
        onChange={asyncSetVenueAutocompleteOptions}
        suggestions={mapAutocompleteOptions(venueAutocompleteOptions)}
        onSelect={handleSelection}
        data-test="input-venue"
        value={controlledValue}
        variant="INSET"
        label={label}
      />
    </div>
  )
}

export default VenueAutocompleteComponent
